/** Input validation shared by the reserve form and the callable functions. */

export interface FieldError { field: string; message: string }

/** Sri Lankan mobiles: 07X XXX XXXX locally, or +947X XXXXXXXX. */
export function normaliseMobile(raw: string): string | null {
  const digits = (raw || '').replace(/[^\d+]/g, '')
  const plain = digits.replace(/^\+/, '')
  if (/^94\d{9}$/.test(plain)) return `+${plain}`
  if (/^0\d{9}$/.test(plain)) return `+94${plain.slice(1)}`
  if (/^\d{9}$/.test(plain) && plain.startsWith('7')) return `+94${plain}`
  return null
}

export function isValidMobile(raw: string): boolean {
  return normaliseMobile(raw) !== null
}

/**
 * Deliberately loose. The job here is to catch a typo before it costs the
 * customer their only notification, not to police what an address may contain —
 * a rejected real address is worse than an accepted unusual one.
 */
export function isValidEmail(raw: string): boolean {
  const value = (raw || '').trim()
  if (value.length < 6 || value.length > MAX_EMAIL) return false
  if (/\s/.test(value)) return false
  return /^[^@]+@[^@.]+(\.[^@.]+)+$/.test(value)
}

export function normaliseEmail(raw: string): string {
  return (raw || '').trim().toLowerCase()
}

export interface ReservationInput {
  firstName: string
  lastName: string
  mobile: string
  email?: string
  note?: string
  qty: number
}

export const MAX_NOTE = 600
export const MAX_EMAIL = 254

export function validateReservation(input: Partial<ReservationInput>): FieldError[] {
  const errors: FieldError[] = []
  const first = (input.firstName || '').trim()
  const last = (input.lastName || '').trim()

  if (first.length < 2) errors.push({ field: 'firstName', message: 'Add your first name.' })
  if (last.length < 2) errors.push({ field: 'lastName', message: 'Add your last name.' })
  if (!isValidMobile(input.mobile || '')) {
    errors.push({ field: 'mobile', message: 'That mobile number does not look right. Use 07X XXX XXXX.' })
  }
  const qty = Math.floor(Number(input.qty ?? 1))
  if (!Number.isFinite(qty) || qty < 1) {
    errors.push({ field: 'qty', message: 'Choose how many you want.' })
  }
  const email = (input.email || '').trim()
  if (email && !isValidEmail(email)) {
    errors.push({ field: 'email', message: 'That email address does not look right.' })
  }
  if ((input.note || '').length > MAX_NOTE) {
    errors.push({ field: 'note', message: `Keep the note under ${MAX_NOTE} characters.` })
  }
  return errors
}

/** Six digits, whitespace tolerated because authenticator apps show "123 456". */
export function isValidTotp(code: string): boolean {
  return /^\d{6}$/.test((code || '').replace(/\s/g, ''))
}

export function normaliseTotp(code: string): string {
  return (code || '').replace(/\s/g, '')
}
